import type { SearchQueryBody } from "@/types";

import type { SortColumn, SortDirection } from "../FileService";
import type { FilesQueryScope } from "../FilesQueryScope";

/**
 * Parameters that participate in a directory query's cache key. Each directory
 * hook passes the subset relevant to it; the object is embedded in the key so
 * TanStack's structural equality distinguishes paginations, searches, sorts,
 * etc. Mirrors the arguments the directory hooks already vary by.
 */
export interface DirectoryQueryKeyParams {
  directoryPath?: string;
  datasetId?: string;
  nextToken?: string;
  pageSize?: number;
  searchTerm?: string;
  showHiddenFiles?: boolean;
  extensions?: string[];
  sortColumn?: SortColumn;
  sortDirection?: SortDirection;
}

/** The parts of an org-wide file search that change which files come back. */
export interface OrgFileSearchKeyParams {
  searchTerm?: string;
  extensions?: string[];
  pageSize?: number;
}

/**
 * Query key factory for file queries. Two namespaces live under the `["files"]`
 * root:
 *
 *  - `org(orgId)` — tenant-scoped *search lists*. The same query yields
 *    different results per org, so the org must be part of the key.
 *  - Everything keyed by a globally-unique resource id (`fileId` / `datasetId`)
 *    hangs directly off the root with NO org. A file — or a dataset's directory
 *    — returns the same data regardless of which org the viewer is currently in
 *    (access is authorized per *user*, and datasets/files can be shared or made
 *    public across orgs). Putting org in these keys would only duplicate the
 *    cache per viewing org and make cross-org invalidations miss.
 *
 * Keys are hierarchical so a shorter key is a prefix of its descendants:
 * `metadata`/`tags` sit under `detail(fileId)`, so invalidating a file's detail
 * also refreshes its derived metadata/tags — while a metadata or tags edit (a
 * deeper key) does not cascade up to the full record.
 */
export const fileQueryKeys = {
  all: () => ["files"] as const,
  org: (orgId: string) => [...fileQueryKeys.all(), "org", orgId] as const,

  // ── Org-scoped search lists (same query → different results per org) ─────
  orgTags: (orgId: string) => [...fileQueryKeys.org(orgId), "tags"] as const,
  orgTagSearch: (orgId: string, search: string) =>
    [...fileQueryKeys.orgTags(orgId), "search", search] as const,
  lists: (orgId: string) => [...fileQueryKeys.org(orgId), "list"] as const,
  listsScope: (orgId: string, scope: FilesQueryScope) =>
    scope.type === "org"
      ? ([...fileQueryKeys.lists(orgId), "org"] as const)
      : ([...fileQueryKeys.lists(orgId), scope.type, scope.id] as const),
  list: (orgId: string, scope: FilesQueryScope, query: SearchQueryBody) =>
    [...fileQueryKeys.listsScope(orgId, scope), query] as const,
  // Every org-wide file search, whatever it was narrowed by. Sits under
  // `lists(orgId)` so an invalidation of the org's file lists refreshes these
  // too, and exists as its own prefix because these are the only entries under
  // `lists` that hold `FileRecord`s: `list()` caches a *query id*, and its rows
  // arrive under the query-results namespace instead. Anything reaching into
  // cached records — see `patchCachedFileRecords` — has to address this and not
  // `lists`, or it is rewriting a payload of a different shape.
  searches: (orgId: string) =>
    [...fileQueryKeys.lists(orgId), "search"] as const,
  // One such search, keyed by everything that narrows it.
  search: (orgId: string, params: OrgFileSearchKeyParams) =>
    [...fileQueryKeys.searches(orgId), params] as const,

  // ── Single-file detail, keyed by globally-unique fileId (no org) ─────────
  details: () => [...fileQueryKeys.all(), "detail"] as const,
  detail: (fileId: string) => [...fileQueryKeys.details(), fileId] as const,
  metadata: (fileId: string) =>
    [...fileQueryKeys.detail(fileId), "metadata"] as const,
  tags: (fileId: string) => [...fileQueryKeys.detail(fileId), "tags"] as const,
  // Sibling of detail: signed URLs have an independent (hourly) lifecycle and
  // should not be refetched just because the record was invalidated.
  signedUrl: (fileId: string) =>
    [...fileQueryKeys.all(), "signedUrl", fileId] as const,

  // ── Directory reads, keyed by globally-unique datasetId (+ params), no org ─
  directories: () => [...fileQueryKeys.all(), "directory"] as const,
  directoryContents: (params?: DirectoryQueryKeyParams) =>
    params === undefined
      ? ([...fileQueryKeys.directories(), "contents"] as const)
      : ([...fileQueryKeys.directories(), "contents", params] as const),
  directoryItemCount: (params?: DirectoryQueryKeyParams) =>
    params === undefined
      ? ([...fileQueryKeys.directories(), "count"] as const)
      : ([...fileQueryKeys.directories(), "count", params] as const),
  directoryExtensions: (params?: DirectoryQueryKeyParams) =>
    params === undefined
      ? ([...fileQueryKeys.directories(), "extensions"] as const)
      : ([...fileQueryKeys.directories(), "extensions", params] as const),
};
